import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion'
import {
  MessageCircle,
  MapPin,
  UtensilsCrossed,
  Coffee,
  Pizza,
  ArrowRight,
  Clock,
} from 'lucide-react'
import { PageMeta } from '@/components/ui/PageMeta'
import { HeroSimple } from '@/components/ui/HeroSimple'
import { Footer } from '@/components/layout/Footer'
import { LocalizedLink } from '@/components/LocalizedLink'
import { cn } from '@/lib/utils'

type VenueKey = 'maia' | 'bar' | 'pizza'

interface VenueConfig {
  key: VenueKey
  icon: typeof UtensilsCrossed
  colorClass: string
  activeBgClass: string
  activeTextClass: string
  bgSoftClass: string
  textClass: string
  borderClass: string
  mapEmbedUrl: string
  directionsUrl: string
  coords: string
}

const venues: VenueConfig[] = [
  {
    key: 'maia',
    icon: UtensilsCrossed,
    colorClass: 'text-maia',
    activeBgClass: 'bg-maia',
    activeTextClass: 'text-white',
    bgSoftClass: 'bg-maia-soft',
    textClass: 'text-maia-dark',
    borderClass: 'border-maia',
    mapEmbedUrl:
      'https://www.google.com/maps?q=45.35095,9.68750&z=16&output=embed',
    directionsUrl: 'https://maps.app.goo.gl/ZRceKAtxCSbXj4Y27',
    coords: '45.35095° N, 9.68750° E',
  },
  {
    key: 'bar',
    icon: Coffee,
    colorClass: 'text-bar-dark',
    activeBgClass: 'bg-bar',
    activeTextClass: 'text-ink',
    bgSoftClass: 'bg-bar-soft',
    textClass: 'text-bar-dark',
    borderClass: 'border-bar-dark',
    mapEmbedUrl:
      'https://www.google.com/maps?q=45.36440,9.69050&z=16&output=embed',
    directionsUrl: 'https://maps.app.goo.gl/PTELeydb72gr9DnK8',
    coords: '45.36440° N, 9.69050° E',
  },
  {
    key: 'pizza',
    icon: Pizza,
    colorClass: 'text-pizza',
    activeBgClass: 'bg-pizza',
    activeTextClass: 'text-white',
    bgSoftClass: 'bg-pizza-soft',
    textClass: 'text-pizza-dark',
    borderClass: 'border-pizza',
    mapEmbedUrl:
      'https://www.google.com/maps?q=45.36390,9.69440&z=16&output=embed',
    directionsUrl: 'https://maps.app.goo.gl/caAbFie3hvwb73eR6',
    coords: '45.36390° N, 9.69440° E',
  },
]

function VenueFinder() {
  const { t } = useTranslation('contacts')
  const shouldReduceMotion = useReducedMotion()
  const [activeKey, setActiveKey] = useState<VenueKey>('maia')
  const activeVenue = venues.find((v) => v.key === activeKey) ?? venues[0]

  const name = t(`venues.cards.${activeVenue.key}.name`)
  const address = t(`venues.cards.${activeVenue.key}.address`)
  const hours = t(`venues.cards.${activeVenue.key}.hours`)

  const contentTransition = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -16 },
        transition: { duration: 0.4, ease: 'easeOut' as const },
      }

  const mapTransition = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.98 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.99 },
        transition: { duration: 0.4, ease: 'easeOut' as const },
      }

  return (
    <div className="mx-auto max-w-6xl">
      {/* Stamp selector */}
      <div
        role="tablist"
        aria-label={t('venues.label')}
        className="relative z-10 mb-6 flex flex-col gap-2 sm:flex-row sm:mb-8 sm:gap-4"
      >
        {venues.map((venue) => {
          const VenueIcon = venue.icon
          const isActive = venue.key === activeKey
          return (
            <button
              key={venue.key}
              role="tab"
              aria-selected={isActive}
              aria-controls={`venue-panel-${venue.key}`}
              id={`venue-tab-${venue.key}`}
              onClick={() => setActiveKey(venue.key)}
              className={cn(
                'group relative flex w-full items-center gap-3 rounded-full px-4 py-3 transition-all duration-300 sm:flex-1 sm:px-5 sm:py-3',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-maia focus-visible:ring-offset-2',
                isActive
                  ? cn('shadow-lg', venue.activeBgClass, venue.activeTextClass)
                  : 'border border-cord bg-cream text-ink hover:border-stone hover:bg-white'
              )}
            >
              <span
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors duration-300 sm:h-9 sm:w-9',
                  isActive ? 'bg-white/25' : venue.bgSoftClass
                )}
                aria-hidden="true"
              >
                <VenueIcon
                  className={cn(
                    'h-4 w-4 transition-colors duration-300',
                    isActive ? 'text-current' : venue.colorClass
                  )}
                  strokeWidth={1.5}
                />
              </span>
              <span className="font-display text-sm font-bold sm:text-base">
                {t(`venues.cards.${venue.key}.name`)}
              </span>
            </button>
          )
        })}
      </div>

      {/* Venue panel */}
      <div
        className="relative overflow-hidden rounded-3xl bg-white shadow-2xl"
        role="tabpanel"
        id={`venue-panel-${activeVenue.key}`}
        aria-labelledby={`venue-tab-${activeVenue.key}`}
      >
        <div className="grid lg:grid-cols-12">
          {/* Info side */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeKey}
              {...contentTransition}
              className="relative order-2 lg:order-1 lg:col-span-5 p-6 sm:p-8 lg:p-10"
            >
              {/* Venue name */}
              <h3 className="font-display text-[clamp(2rem,5vw,3.25rem)] font-extrabold leading-[0.95] tracking-tight text-ink">
                {name}
              </h3>

              {/* Address */}
              <address className="mt-6 not-italic">
                <a
                  href={activeVenue.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/address flex items-start gap-3 text-base leading-relaxed text-stone transition-colors hover:text-ink"
                >
                  <MapPin
                    className={cn(
                      'mt-0.5 h-5 w-5 shrink-0 transition-transform duration-300 group-hover/address:-translate-y-0.5',
                      activeVenue.colorClass
                    )}
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                  <span>{address}</span>
                </a>
              </address>

              {/* Hours */}
              <div className="mt-5 flex items-start gap-3 text-sm text-muted">
                <Clock
                  className="mt-0.5 h-5 w-5 shrink-0"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
                <p className="whitespace-pre-line leading-relaxed">{hours}</p>
              </div>

              {/* CTAs */}
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={activeVenue.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-cord bg-white px-5 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-cream"
                >
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                  {t(`venues.cards.${activeVenue.key}.ctaMap`)}
                </a>
              </div>

              {/* Link to venue page */}
              <LocalizedLink
                to={activeVenue.key}
                className={cn(
                  'mt-5 inline-flex items-center gap-1 text-sm font-semibold transition-colors',
                  activeVenue.colorClass,
                  'hover:underline underline-offset-4'
                )}
              >
                {t(`venues.cards.${activeVenue.key}.ctaPage`)}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </LocalizedLink>
            </motion.div>
          </AnimatePresence>

          {/* Map */}
          <div className="relative order-1 h-72 sm:h-96 lg:order-2 lg:col-span-7 lg:h-auto lg:min-h-[480px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeKey}
                {...mapTransition}
                className="relative h-full w-full"
              >
                <a
                  href={activeVenue.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('venues.openInMaps', { venue: name })}
                  className="absolute inset-0 z-10"
                >
                  <span className="sr-only">{t('venues.openInMaps')}</span>
                </a>
                <iframe
                  src={activeVenue.mapEmbedUrl}
                  title={t('venues.mapTitle', { venue: name })}
                  className="pointer-events-none block h-full w-full"
                  style={{ border: 0 }}
                  loading="eager"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}

export function Contacts() {
  const { t, i18n } = useTranslation('contacts')
  const shouldReduceMotion = useReducedMotion()
  const lang = i18n.language as 'it' | 'en'

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const canonicalPath = `/${lang}/${lang === 'it' ? 'contatti' : 'contacts'}`

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      name: 'Maia e Tas',
      image: 'https://maiaetas.it/media/logo-maia.jpg',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Via Piacenza 97',
        addressLocality: 'Crema',
        addressRegion: 'CR',
        postalCode: '26013',
        addressCountry: 'IT',
      },
      telephone: '+39-333-702-4046',
      url: `https://maiaetas.it/${lang}/maia-e-tas`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BarOrPub',
      name: 'Bar e Tas',
      image: 'https://maiaetas.it/media/logo-bar.jpg',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Via Ponte della Crema 1/2',
        addressLocality: 'Crema',
        addressRegion: 'CR',
        postalCode: '26013',
        addressCountry: 'IT',
      },
      telephone: '+39-333-702-4046',
      url: `https://maiaetas.it/${lang}/bar-e-tas`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      name: 'Pizza e Tas',
      image: 'https://maiaetas.it/media/logo-pizza.jpg',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Piazzale Rimembranze',
        addressLocality: 'Crema',
        addressRegion: 'CR',
        postalCode: '26013',
        addressCountry: 'IT',
      },
      telephone: '+39-333-702-4046',
      url: `https://maiaetas.it/${lang}/pizza-e-tas`,
    },
  ]

  const phone = t('whatsapp.number')
  const whatsappUrl = `https://wa.me/${phone.replace(/\D/g, '')}`

  return (
    <>
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        canonicalPath={canonicalPath}
        jsonLd={jsonLd}
      />

      <main id="main-content">
        <HeroSimple
          backgroundColor="bg-maia"
          textColor="text-white"
          icon={MapPin}
          title={t('hero.title')}
          className="min-h-[55vh] md:min-h-[60vh]"
        />

        {/* Venue finder */}
        <section className="bg-paper px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div {...fadeUp} className="mb-10 mx-auto max-w-2xl text-center lg:mb-14">
              <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-bold leading-tight tracking-tight text-ink">
                {t('venues.title')}
              </h2>
              <p className="mt-3 text-lg text-stone">{t('venues.description')}</p>
            </motion.div>

            <VenueFinder />
          </div>
        </section>

        {/* WhatsApp band */}
        <section className="bg-maia px-4 py-16 text-center text-white sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mx-auto max-w-3xl text-center">
            <h2 className="text-[clamp(1.75rem,4vw,3rem)] font-bold leading-tight tracking-tight">
              {t('whatsapp.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-white/90">{t('whatsapp.subtitle')}</p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('whatsapp.aria')}
              className="mt-8 inline-flex items-center justify-center gap-3 rounded-full bg-white px-8 py-3 text-sm font-semibold text-maia shadow-lg transition-colors hover:bg-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              {t('whatsapp.cta')}
            </a>
            <p className="mt-4 text-sm text-white/70">{phone}</p>
          </motion.div>
        </section>
      </main>

      <Footer variant="maia" />
    </>
  )
}
